/**
 * Reload policy for service-worker updates.
 *
 * main.js activates a freshly installed worker at once (skipWaiting) and
 * reloads on `controllerchange` so the page runs the new version. Two cases
 * need care, and this is where they are decided so they can be tested apart
 * from main.js, which registers the worker as a side effect of being imported:
 *
 * 1. The very first visit. sw.js calls clients.claim() on activate, which
 *    fires controllerchange on a page that had no controller at all. There is
 *    no newer version to pick up — this page IS the newest — so reloading only
 *    makes the first load happen twice.
 * 2. The page is busy. A reload mid-recording throws the take away, and one
 *    during an import, export or transcription discards that work. The reload
 *    waits until the editor reports itself idle.
 *
 * @param {object} opts
 * @param {boolean} opts.hadController - navigator.serviceWorker.controller was
 *   set when the page loaded
 * @param {() => boolean} [opts.isBusy] - work in progress that a reload would lose
 * @param {() => void} opts.reload
 * @param {number} [opts.retryMs] - how often to re-check while busy
 * @param {(fn: () => void, ms: number) => void} [opts.schedule]
 * @returns {() => void} the controllerchange listener
 */
export function createControllerChangeHandler({
  hadController,
  isBusy = () => false,
  reload,
  retryMs = 5000,
  schedule = (fn, ms) => setTimeout(fn, ms)
}) {
  let armed = !!hadController;
  let pending = false;

  return function onControllerChange() {
    // The worker just claimed a page it did not control: first install.
    if (!armed) {
      armed = true;
      return;
    }
    if (pending) return;
    pending = true;

    const attempt = () => {
      if (isBusy()) {
        schedule(attempt, retryMs);
        return;
      }
      reload();
    };
    attempt();
  };
}
